import {
  Autocomplete,
  type AutocompleteFreeSoloValueMapping,
  type AutocompleteProps,
  type AutocompleteValue,
  TextField,
} from '@mui/material';
import {
  /**
   * This `useEffect` usage has been reviewed and documented.
   */
  // eslint-disable-next-line no-restricted-imports
  useEffect,
  useId,
  useState,
} from 'react';

import { FormField, isDefined, useDebounce } from 'common';

import { useReactFlowViewContext } from '../../../context/context';
import { type ConfigFieldProps } from '../types';
import { useSelectedNodeField } from '../useSelectedNodeField';

const DEFAULT_FREE_SOLO_HELPER_TEXT = 'Pick from available options or type to add a new one';

interface FieldProps<
  TValue,
  TMultiple extends boolean | undefined,
  TDisableClearable extends boolean | undefined,
  TFreeSolo extends boolean | undefined,
>
  extends
    Omit<
      AutocompleteProps<TValue, TMultiple, TDisableClearable, TFreeSolo>,
      'renderInput' | 'onChange' | 'value'
    >,
    ConfigFieldProps {
  label: string;
  helperText?: string;
  options: TValue[];
  getOptionLabel?: (option: TValue | AutocompleteFreeSoloValueMapping<TFreeSolo>) => string;
  multiple?: TMultiple;
  disableClearable?: TDisableClearable;
  /**
   * onDebouncedInputEffect is a dependency on a `useEffect`, and therefore must be memoized
   * We recommend passing in the `useState` setter directly.
   */
  onDebouncedInputEffect?: (input: string) => void;
  /**
   * If you want `freeSolo`, you will want extra functionality.
   * Use BlockFreeSoloMultiAutocomplete instead.
   * @see {./FreeSoloMultiAutocomplete}
   */
  freeSolo?: TFreeSolo;
  onChange?: (value: AutocompleteValue<TValue, TMultiple, TDisableClearable, TFreeSolo>) => void;
}

export function BlockAutocomplete<
  TValue,
  TMultiple extends boolean | undefined,
  TDisableClearable extends boolean | undefined,
  TFreeSolo extends boolean | undefined,
>(props: FieldProps<TValue, TMultiple, TDisableClearable, TFreeSolo>) {
  const {
    fieldName,
    label,
    getOptionLabel,
    getOptionKey,
    helperText,
    onDebouncedInputEffect,
    multiple,
    ...fieldProps
  } = props;
  const { isEditable } = useReactFlowViewContext();
  type FieldAutocompleteValue = AutocompleteValue<TValue, TMultiple, TDisableClearable, TFreeSolo>;
  const { field, visibleError, setValue } = useSelectedNodeField<FieldAutocompleteValue>(fieldName);
  const [input, setInput] = useState<string>('');
  const debouncedInput = useDebounce(input);
  useEffect(() => {
    /**
     * This effect attempts to sync the debounced input value with an external state.
     * `useEffect` review
     * Guidelines: https://notable-health.atlassian.net/wiki/spaces/notableengineering/pages/2566389961/React+State+Management#Required-Review
     *
     * This has been reviewed by a UI tech lead. It is a bit questionable, but approved.
     * It runs the risk of destroying upstream state, and potentially causing infinite loops, but in practice it has not been observed to do so.
     */
    onDebouncedInputEffect?.(debouncedInput);
  }, [debouncedInput, onDebouncedInputEffect]);

  const fieldId = useId();
  return (
    <FormField
      id={fieldId}
      label={label}
      helperText={props.freeSolo ? (helperText ?? DEFAULT_FREE_SOLO_HELPER_TEXT) : helperText}
      error={visibleError}
    >
      <Autocomplete
        id={fieldId}
        {...fieldProps}
        disabled={!isEditable}
        multiple={multiple}
        value={
          multiple
            ? Array.isArray(field.value)
              ? field.value
              : isDefined(field.value)
                ? ([field.value] as FieldAutocompleteValue)
                : ([] as FieldAutocompleteValue)
            : (field.value ?? (null as FieldAutocompleteValue))
        }
        onChange={(_event, newValue) => {
          setValue(newValue);
          fieldProps.onChange?.(newValue);
        }}
        onInputChange={(_event, newInputValue) => {
          setInput(newInputValue);
        }}
        getOptionLabel={getOptionLabel}
        renderInput={(params) => <TextField {...params} size="small" />}
        renderOption={(props, option, state) => (
          /**
           * `renderOption` is required to ensure that the filtered items have unique keys.
           * See this article: https://lockstep.io/blog/debugging-lesson-well-learned-fixing-material-ui-autocomplete-search/
           */
          <li {...props} key={state.index}>
            {getOptionLabel?.(option) ?? String(option)}
          </li>
        )}
      />
    </FormField>
  );
}
